import { dirname, basename } from "path";
import { runInAction } from "mobx";
import { initComponentStore, noop } from "@/utils/mobx";
import {
    SandboxStore,
    SandboxSessionStore,
} from "@/app_store/agent";
import { appStore } from "@/app_store";

export type GitFileStatus = "added" | "modified";

export interface BrowsableEntry {
    path: string;
    is_dir: boolean;
}


export class FileBrowserStore {
    onSelect: (path: string | null, isDir: boolean) => any = noop;
    onOpen: (path: string, isDir: boolean) => any = noop;
    onContextMenu: (path: string, isDir: boolean, x: number, y: number) => any = noop;
    mode: "flat" | "tree" = "flat";
    rootPath: string = "";
    sandbox: SandboxStore | null = null;

    history: string[] = [""];
    historyIndex: number = 0;
    selectedPath: string | null = null;
    expandedPaths: Set<string> = new Set();
    activePath: string | null = null;
    filterChanged: boolean = false;
    renderActions: (path: string, isDir: boolean) => any = noop;

    private _sessionId: string | null = null;

    constructor() {
        initComponentStore(this);
    }

    setSandbox(sandbox: SandboxStore | null) {
        if (this.sandbox === sandbox) return;

        if (this._sessionId != null && this.sandbox) {
            this.sandbox.destroy_session(this._sessionId).catch(() => {});
        }
        this._sessionId = null;
        this.sandbox = sandbox;
        this.expandedPaths = new Set();

        if (sandbox) void this._initSession();
    }

    dispose() {
        const sandbox = this.sandbox;
        const sessionId = this._sessionId;
        this._sessionId = null;
        if (sandbox && sessionId != null) sandbox.destroy_session(sessionId).catch(() => {});
    }

    private _session(): SandboxSessionStore | null {
        if (this._sessionId == null) return null;
        return this.sandbox?.sessions[this._sessionId] ?? null;
    }

    private _watchTargets(): Set<string> {
        return new Set([this.rootPath, this.currentPath, ...this.expandedPaths]);
    }

    private async _initSession() {
        const sandbox = this.sandbox;
        if (!sandbox) return;
        const created: { id: string } = await sandbox.create_session();
        runInAction(() => { this._sessionId = created.id; });
        this._updateWatchers();
    }

    private _updateWatchers() {
        this._session()?.watch_directories(Array.from(this._watchTargets()));
    }

    private _isDirLoaded(path: string): boolean {
        return this._session()?.directories[path] !== undefined;
    }

    get currentPath(): string {
        return this.history[this.historyIndex];
    }

    get canGoBack(): boolean {
        return this.historyIndex > 0;
    }

    get canGoForward(): boolean {
        return this.historyIndex < this.history.length - 1;
    }

    get canGoUp(): boolean {
        return this.currentPath !== this.rootPath;
    }

    getSortedDirectoryFiles(path: string): BrowsableEntry[] {
        const source: BrowsableEntry[] = this._session()?.directories[path]?.entries ?? [];
        const entries = source.slice().sort((a, b) => {
            if (a.is_dir !== b.is_dir) return a.is_dir ? -1 : 1;
            return a.path.localeCompare(b.path);
        });
        if (!this.filterChanged) return entries;
        const statusMap = this.gitStatusMap;
        return entries.filter(e => statusMap.has(e.path));
    }

    get currentName(): string {
        return basename(this.currentPath) || "/";
    }

    get loadingPaths(): Set<string> {
        return new Set(
            [this.rootPath, ...Array.from(this.expandedPaths)].filter(p => !this._isDirLoaded(p))
        );
    }

    isDirectoryLoading(path: string): boolean {
        return this.loadingPaths.has(path);
    }

    get gitStatusMap(): Map<string, GitFileStatus> {
        const map = new Map<string, GitFileStatus>();
        const changes = appStore.git.changes;
        if (!changes) return map;

        const setIfDominant = (path: string, status: GitFileStatus) => {
            const existing = map.get(path);
            if (!existing || (existing === "added" && status === "modified")) {
                map.set(path, status);
            }
        };

        for (const entry of changes) {
            const isAdded = entry.labels.length > 0 && entry.labels.every(l => l === "index_new" || l === "wt_new");
            const status: GitFileStatus = isAdded ? "added" : "modified";

            setIfDominant(entry.path, status);

            let dir = dirname(entry.path);
            while (dir && dir !== ".") {
                setIfDominant(dir, status);
                const parent = dirname(dir);
                if (parent === dir) break;
                dir = parent;
            }
        }

        return map;
    }

    setRootPath(newRoot: string) {
        this.rootPath = newRoot;
        this.history = [newRoot];
        this.historyIndex = 0;
        this.selectedPath = null;
        this.expandedPaths = new Set();
        this._updateWatchers();
    }

    setActivePath(path: string | null) {
        this.activePath = path;
        if (path) this.expandToPath(path);
    }

    expandToPath(path: string) {
        let current = dirname(path);
        while (current !== this.rootPath && current !== "." && current !== "") {
            this.expandedPaths.add(current);
            const parent = dirname(current);
            if (parent === current) break;
            current = parent;
        }
        this.selectedPath = path;
        this._updateWatchers();
    }

    toggleExpand(path: string) {
        if (this.expandedPaths.has(path)) {
            this.expandedPaths.delete(path);
        } else {
            this.expandedPaths.add(path);
        }
        this._updateWatchers();
    }

    collapseToDepth(maxDepth: number) {
        const rootSegments = this.rootPath.split("/").filter(Boolean).length;
        for (const path of Array.from(this.expandedPaths)) {
            const pathDepth = path.split("/").filter(Boolean).length - rootSegments - 1;
            if (pathDepth >= maxDepth) {
                this.expandedPaths.delete(path);
            }
        }
        this._updateWatchers();
    }

    navigate(path: string) {
        this.history = this.history.slice(0, this.historyIndex + 1);
        this.history.push(path);
        this.historyIndex++;
        this.selectedPath = null;
        this.onSelect(null, false);
        this._updateWatchers();
    }

    back() {
        if (this.canGoBack) {
            this.historyIndex--;
            this.onSelect(null, false);
            this._updateWatchers();
        }
    }

    forward() {
        if (this.canGoForward) {
            this.historyIndex++;
            this.onSelect(null, false);
            this._updateWatchers();
        }
    }

    up() {
        if (this.canGoUp) {
            const parent = dirname(this.currentPath);
            this.navigate(parent === "." ? "" : parent);
        }
    }

    select(path: string, isDir: boolean) {
        this.selectedPath = path;
        this.onSelect(path, isDir);
    }
}
